import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency, toUSD } from "@/lib/currency";
import PageHeader from "@/components/PageHeader";
import Card from "@/components/Card";
import EntityDetailDrawer from "@/components/EntityDetailDrawer";
import CurrencySelect from "@/components/CurrencySelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/format";
import { Plus, TrendingUp, Trash2, Pencil } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const EMPTY = { name: "", type: "Marketing", monthly_retainer: 0, total_spend: 0, attributed_revenue: 0, contact_name: "", email: "", notes: "" };

export default function Agencies() {
  const { fmtMoney } = useCurrency();
  const [agencies, setAgencies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [currency, setCurrency] = useState("IDR");
  const [drawerId, setDrawerId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const load = () => api.get("/agencies").then((r) => setAgencies(r.data || []));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditItem(null); setForm(EMPTY); setCurrency("IDR"); setOpen(true); };
  const openEdit = (a, e) => {
    e.stopPropagation();
    setEditItem(a);
    setForm({ name: a.name, type: a.type, monthly_retainer: a.monthly_retainer, total_spend: a.total_spend, attributed_revenue: a.attributed_revenue, contact_name: a.contact_name || "", email: a.email || "", notes: a.notes || "" });
    setCurrency("IDR"); // display and input values are always in rupiah
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name) return toast.error("Name is required");
    const payload = {
      ...form,
      monthly_retainer: toUSD(+form.monthly_retainer, currency),
      total_spend: toUSD(+form.total_spend, currency),
      attributed_revenue: toUSD(+form.attributed_revenue, currency),
    };
    if (editItem) {
      await api.put(`/agencies/${editItem.id}`, { ...editItem, ...payload });
      toast.success("Agency updated");
    } else {
      await api.post("/agencies", payload);
      toast.success("Agency added");
    }
    setOpen(false);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/agencies/${id}`);
    toast.success("Agency removed");
    load();
  };

  const moneyFields = [
    ["monthly_retainer", "Monthly retainer"],
    ["total_spend", "Total spend"],
    ["attributed_revenue", "Attributed revenue"],
  ];
  const textFields = [
    ["name", "Name", "text"],
    ["type", "Type", "text"],
    ["contact_name", "Contact name", "text"],
    ["email", "Email", "email"],
  ];

  const isConverting = false;

  return (
    <div data-testid="agencies-page" className="max-w-[1400px] mx-auto">
      <PageHeader
        title="Agencies"
        subtitle="Marketing & external partners"
        action={
          <Button data-testid="add-agency-btn" onClick={openCreate} className="rounded-full bg-black text-white hover:bg-black/85">
            <Plus className="w-4 h-4 mr-1.5" /> Add agency
          </Button>
        }
      />

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditItem(null); }}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <div className="flex items-center justify-between pr-6">
              <DialogTitle>{editItem ? "Edit agency" : "New agency"}</DialogTitle>
              <div className="flex items-center gap-2 text-[11.5px] text-[#8E8E93]">
                Currency <CurrencySelect value={currency} onChange={setCurrency} size="sm" />
              </div>
            </div>
          </DialogHeader>
          <div className="grid gap-3 max-h-[70vh] overflow-y-auto pr-1">
            {textFields.map(([k, l, t]) => (
              <div key={k} className="grid gap-1.5">
                <Label className="text-[12px]">{l}</Label>
                <Input data-testid={`agency-input-${k}`} type={t} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="rounded-xl" />
              </div>
            ))}
            <div className="border-t border-black/[0.05] pt-3 grid gap-3">
              <div className="text-[10.5px] text-[#8E8E93] uppercase tracking-wider">Financial figures in {currency}</div>
              {moneyFields.map(([k, l]) => (
                <div key={k} className="grid gap-1.5">
                  <Label className="text-[12px]">{l}</Label>
                  <Input
                    data-testid={`agency-input-${k}`}
                    type="number" step="0.01"
                    value={form[k]}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="rounded-xl font-mono"
                  />
                  {isConverting && +form[k] > 0 && (
                    <div className="text-[10.5px] text-[#8E8E93]">
                      ≈ {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(toUSD(+form[k], currency))} USD
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="save-agency-btn" onClick={submit} className="rounded-full">
              {editItem ? "Save changes" : "Save agency"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {agencies.map((a) => {
            const roas = a.total_spend > 0 ? (a.attributed_revenue / a.total_spend).toFixed(2) : "—";
            return (
              <button type="button" key={a.id} data-testid={`agency-${a.id}`} onClick={() => setDrawerId(a.id)} className="text-left p-5 rounded-2xl bg-white border border-black/[0.04] card-hover">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-[#A18E9F] font-semibold">{a.type}</div>
                    <div className="mt-1 font-display font-semibold text-[16px]">{a.name}</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button onClick={(e) => openEdit(a, e)} className="text-[#8E8E93] hover:text-[#1C1C1E] transition-colors"><Pencil className="w-4 h-4" /></button>
                    <button onClick={(e) => { e.stopPropagation(); setConfirmId(a.id); }} className="text-[#8E8E93] hover:text-[#FF3B30] transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 text-[12px]">
                  <div><div className="text-[10px] uppercase tracking-wider text-[#8E8E93]">Retainer / mo</div><div className="mt-0.5 font-mono font-semibold">{fmtMoney(a.monthly_retainer)}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wider text-[#8E8E93]">Total spend</div><div className="mt-0.5 font-mono font-semibold">{fmtMoney(a.total_spend)}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wider text-[#8E8E93]">Attrib. revenue</div><div className="mt-0.5 font-mono font-semibold text-[#34C759]">{fmtMoney(a.attributed_revenue)}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wider text-[#8E8E93]">ROAS</div><div className="mt-0.5 font-mono font-semibold flex items-center gap-1"><TrendingUp className="w-3 h-3 text-[#34C759]" /> {roas}x</div></div>
                </div>
              </button>
            );
          })}
          {agencies.length === 0 && <div className="col-span-full py-8 text-center text-[#8E8E93]">No agencies yet.</div>}
        </div>
      </Card>
      <EntityDetailDrawer entityType="agency" entityId={drawerId} open={!!drawerId} onOpenChange={(v) => !v && setDrawerId(null)} />

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove agency?</AlertDialogTitle>
            <AlertDialogDescription>This agency will be permanently removed. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white"
              onClick={() => { remove(confirmId); setConfirmId(null); }}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
